import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { applyConsent, CONSENT_STORAGE_KEY } from "@/lib/analytics";

/**
 * Custom cookie banner + Google Consent Mode v2 controller.
 *
 * Behaviour:
 *   - First visit: banner is shown; Google Consent Mode defaults (set in
 *     index.html) keep all four Google signals `denied` until the visitor
 *     accepts.
 *   - "Accept toate" → localStorage saves "all" and gtag consent is
 *     updated to `granted` for ad_storage, analytics_storage,
 *     ad_user_data, ad_personalization.
 *   - "Doar esențiale" → localStorage saves "essential" and the four
 *     signals are explicitly re-affirmed as `denied`.
 *   - Returning visits: the saved value is already replayed at page load
 *     (see index.html), and the banner does not re-appear.
 *   - Anywhere on the site, a "Setări cookie" link can dispatch the
 *     `ds:cookie-reopen` window event to re-open this banner so the
 *     visitor can change their preference.
 */
export default function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(CONSENT_STORAGE_KEY)) setShow(true);
    const reopen = () => setShow(true);
    window.addEventListener("ds:cookie-reopen", reopen);
    return () => window.removeEventListener("ds:cookie-reopen", reopen);
  }, []);

  const decide = (choice) => {
    localStorage.setItem(CONSENT_STORAGE_KEY, choice);
    applyConsent(choice);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-16 lg:bottom-4 left-4 right-4 lg:right-4 lg:left-auto lg:max-w-md z-50" data-testid="cookie-consent">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-5">
        <h3 className="font-display font-bold text-[#0F2C59]">Cookie-uri</h3>
        <p className="text-sm text-slate-600 mt-2">
          Folosim cookie-uri esențiale pentru funcționarea site-ului. Cookie-urile analitice sau de publicitate se activează doar cu acordul dumneavoastră.{" "}
          <Link to="/politica-cookies" className="underline text-[#0F2C59]">Detalii</Link>
        </p>
        <div className="flex gap-2 mt-4">
          <Button onClick={() => decide("essential")} variant="outline" className="rounded-full flex-1" data-testid="cookie-essential-btn">
            Doar esențiale
          </Button>
          <Button onClick={() => decide("all")} className="rounded-full flex-1 bg-[#0F2C59] hover:bg-[#163d7a]" data-testid="cookie-accept-all-btn">
            Accept toate
          </Button>
        </div>
      </div>
    </div>
  );
}
